import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { fmtCurrency, fmtCurrencyWhole, fmtMonth } from '../lib/fmt';
import type { BudgetTimeline, BudgetTimelinePoint } from '../lib/api';
import { EmptyState } from './ui';

// ─── Contractual ceiling vs. expenditure over time ──────────────────────────
//
// The reader has ONE question: how close is spend to the line we're held to,
// and did a change order move that line before or after we crossed it?
//
// So cumulative burn is the subject — a single emphasised line — and the
// contract band is drawn as recessive stacked ZONES behind it rather than four
// competing threshold lines. Reading "which zone is the line in" is the same
// act as reading which flag has fired, and it keeps the only two saturated
// marks (burn, and the hard limit) far enough apart to stay distinguishable
// for colour-blind readers. Four peer-coloured threshold lines could not:
// amber and red at equal weight land below the readable separation floor.
//
// Zones are stacked deltas, not absolute values — recharts stacks them, so
// each band's height is the gap between one threshold and the next, and the
// top of the stack is the outermost contractual figure. They use stepAfter so
// a change order reads as a STEP in the month it takes effect rather than a
// slope smeared across the months either side of it.

const ZONE_FILL = ['var(--chart-zone-1)', 'var(--chart-zone-2)', 'var(--chart-zone-3)', 'var(--chart-zone-4)'];

interface ChartRow extends BudgetTimelinePoint {
  /** Stacked band heights, innermost → outermost. */
  zone1: number | null;
  zone2: number | null;
  zone3: number | null;
  zone4: number | null;
  /** Outermost contractual figure — the hard limit line. */
  limit: number | null;
}

/** Zone labels differ by contract type: a fixed-fee engagement has one line,
 *  not a band. Returned in stack order (innermost first). */
function zoneLabels(contractType: BudgetTimeline['contract_type']): string[] {
  if (contractType === 'fixed_fee') return ['Within fixed fee'];
  if (contractType === 'estimate') return ['Within low', 'Low → median', 'Median → high'];
  return ['Within low', 'Low → median', 'Median → high', 'High → shall-not-exceed'];
}

/** The outermost figure — the one that is a breach to cross, not a warning. */
function limitLabel(contractType: BudgetTimeline['contract_type']): string {
  if (contractType === 'fixed_fee') return 'Fixed fee';
  if (contractType === 'estimate') return 'High estimate';
  return 'Shall-not-exceed';
}

export function BudgetTimelineChart({ timeline }: { timeline: BudgetTimeline }) {
  const { rows, labels, limitName, hasContract } = useMemo(() => {
    const type = timeline.contract_type;
    // The chart's SHAPE comes from the data, not the contract type: an estimate
    // contract can acquire a shall-not-exceed ceiling by change order, and
    // keying off the type alone would leave that ceiling undrawn on the one
    // chart whose job is showing burn against it.
    const shape: BudgetTimeline['contract_type'] =
      type === 'fixed_fee' || type == null ? type
        : timeline.points.some((p) => p.not_to_exceed != null) ? 'estimate_sne'
        : 'estimate';
    const labels = zoneLabels(shape);
    const rows: ChartRow[] = timeline.points.map((p) => {
      // Band edges in ascending order for this contract shape. A fixed-fee
      // contract has a single edge; the estimate shapes have three or four.
      const edges: Array<number | null> = shape === 'fixed_fee'
        ? [p.fixed_fee]
        : shape === 'estimate'
          ? [p.estimate_low, p.estimate_median, p.estimate_high]
          : [p.estimate_low, p.estimate_median, p.estimate_high, p.not_to_exceed];

      // Convert edges to stacked heights: each zone is the gap above the one
      // below it, so the rendered stack tops out at the outermost figure.
      const heights: Array<number | null> = [];
      let prev = 0;
      for (const e of edges) {
        if (e == null) { heights.push(null); continue; }
        heights.push(Math.max(0, e - prev));
        prev = e;
      }
      // Outermost NON-NULL edge for this month. In the months before an SNE
      // amendment takes effect the binding ceiling is still the high estimate —
      // which is what ceilingOf() reports too — so taking the last edge
      // unconditionally would blank the limit line for the whole period before
      // the ceiling existed.
      let limit: number | null = null;
      for (const e of edges) if (e != null) limit = e;
      return {
        ...p,
        zone1: heights[0] ?? null,
        zone2: heights[1] ?? null,
        zone3: heights[2] ?? null,
        zone4: heights[3] ?? null,
        limit,
      };
    });
    return {
      rows,
      labels,
      limitName: limitLabel(shape),
      hasContract: type != null,
    };
  }, [timeline]);

  if (rows.length === 0) {
    return <EmptyState message="No worklogs or contract dates to plot yet." />;
  }

  // Months where a change order takes effect, so a step is attributable to the
  // change order that caused it instead of reading as an unexplained jump.
  const coMonths = new Map(timeline.change_orders.map((co) => [co.month, co]));

  return (
    <div>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              tickFormatter={fmtMonth}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              width={58}
              tickFormatter={(v) => fmtCurrencyWhole(Number(v))}
            />
            <Tooltip content={<TimelineTooltip coMonths={coMonths} limitName={limitName} />} />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              iconType="plainline"
              iconSize={10}
            />

            {/* Contract zones — recessive background, stacked innermost-out. */}
            {hasContract && labels.map((label, i) => (
              <Area
                key={label}
                type="stepAfter"
                dataKey={`zone${i + 1}`}
                name={label}
                stackId="contract"
                stroke="none"
                fill={ZONE_FILL[i]}
                fillOpacity={1}
                isAnimationActive={false}
                legendType="square"
              />
            ))}

            {/* The hard limit. Crossing this is a breach of terms, not a
                warning, so it gets its own emphasised line on top of the band
                rather than just being the top edge of a fill. */}
            {hasContract && (
              <Line
                type="stepAfter"
                dataKey="limit"
                name={limitName}
                stroke="var(--chart-limit)"
                strokeWidth={2}
                strokeDasharray="5 3"
                dot={false}
                isAnimationActive={false}
              />
            )}

            {/* Change-order steps, marked on the axis so each step in the band
                is attributable. */}
            {timeline.change_orders.map((co) => (
              <ReferenceLine
                key={co.id}
                x={co.month}
                stroke="var(--color-text-hint)"
                strokeDasharray="2 3"
                strokeWidth={1}
              />
            ))}

            {/* The subject. Solid, heaviest mark, drawn last so it sits on top. */}
            <Line
              type="monotone"
              dataKey="burned_cumulative"
              name="Cumulative burn"
              stroke="var(--chart-burn)"
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {timeline.change_orders.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: 'var(--color-text-muted)' }}>
          Dotted verticals mark change orders. Steps in the band are the change
          order taking effect that month.
        </div>
      )}
    </div>
  );
}

// ─── Tooltip ────────────────────────────────────────────────────────────────
// Shows the two numbers that matter — burn and the limit — plus the headroom
// between them, rather than echoing every stacked band height (which are
// deltas and would read as nonsense on their own).

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  label?: string;
  coMonths: Map<string, BudgetTimeline['change_orders'][number]>;
  limitName: string;
}

function TimelineTooltip({ active, payload, label, coMonths, limitName }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const co = label ? coMonths.get(label) : undefined;
  const headroom = row.limit != null ? row.limit - row.burned_cumulative : null;

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '0.5px solid var(--color-border-strong)',
      borderRadius: 'var(--radius-sm)',
      padding: '8px 10px',
      fontSize: 11,
      maxWidth: 260,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>
        {label ? fmtMonth(label) : ''}
        {row.is_future && (
          <span style={{ marginLeft: 6, color: 'var(--color-text-muted)', fontWeight: 400 }}>
            projected
          </span>
        )}
      </div>
      <Row label="Burned (cumulative)" value={fmtCurrency(row.burned_cumulative)} />
      {row.burned_in_month > 0 && (
        <Row label="Burned this month" value={fmtCurrency(row.burned_in_month)} muted />
      )}
      {row.limit != null && <Row label={limitName} value={fmtCurrency(row.limit)} />}
      {headroom != null && (
        <Row
          label={headroom >= 0 ? 'Headroom' : 'Over by'}
          value={fmtCurrency(Math.abs(headroom))}
          tone={headroom >= 0 ? undefined : 'red'}
        />
      )}
      {co && (
        <div style={{
          marginTop: 6, paddingTop: 6,
          borderTop: '0.5px solid var(--color-border)',
          color: 'var(--color-text-muted)',
        }}>
          <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>
            {co.reference ? `${co.reference} — ` : 'Change order — '}
            {co.is_effective ? 'effective' : 'takes effect'} {co.effective_date}
          </div>
          <div>{co.scope_change}</div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, tone }: { label: string; value: string; muted?: boolean; tone?: 'red' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <strong style={{
        color: tone === 'red' ? 'var(--color-red-text)' : muted ? 'var(--color-text-muted)' : 'var(--color-text)',
        fontWeight: muted ? 400 : 500,
      }}>
        {value}
      </strong>
    </div>
  );
}
